import type { ControllerLayout } from "./gamepad-mapping";

export type VisualCluster = "leftStick" | "rightStick" | "dpad" | "face" | "system" | "rear";

export interface VisualPoint {
  x: number;
  y: number;
}

export interface ControllerVisualPreset {
  name: string;
  body: "ultimate" | "retro" | "xbox" | "dualsense" | "generic";
  positions: Record<VisualCluster, VisualPoint>;
}

export type VisualLayoutOverrides = Partial<
  Record<ControllerLayout, Partial<Record<VisualCluster, VisualPoint>>>
>;

const ASYMMETRIC_POSITIONS: Record<VisualCluster, VisualPoint> = {
  leftStick: { x: 20, y: 22 },
  rightStick: { x: 53, y: 48 },
  dpad: { x: 32, y: 47 },
  face: { x: 69, y: 17 },
  system: { x: 43, y: 21 },
  rear: { x: 30, y: 84 },
};

const SYMMETRIC_POSITIONS: Record<VisualCluster, VisualPoint> = {
  leftStick: { x: 31, y: 49 },
  rightStick: { x: 56, y: 49 },
  dpad: { x: 19, y: 20 },
  face: { x: 68, y: 18 },
  system: { x: 43, y: 26 },
  rear: { x: 30, y: 84 },
};

export const CONTROLLER_VISUAL_PRESETS: Record<ControllerLayout, ControllerVisualPreset> = {
  "8bitdo-ultimate-2": {
    name: "8BitDo Ultimate 2",
    body: "ultimate",
    positions: ASYMMETRIC_POSITIONS,
  },
  "8bitdo-pro-3": {
    name: "8BitDo Pro 3",
    body: "retro",
    positions: SYMMETRIC_POSITIONS,
  },
  "8bitdo": {
    name: "8BitDo asymmetric",
    body: "ultimate",
    positions: ASYMMETRIC_POSITIONS,
  },
  xbox: {
    name: "Xbox",
    body: "xbox",
    positions: ASYMMETRIC_POSITIONS,
  },
  playstation: {
    name: "PlayStation",
    body: "dualsense",
    positions: SYMMETRIC_POSITIONS,
  },
  generic: {
    name: "Generic asymmetric",
    body: "generic",
    positions: ASYMMETRIC_POSITIONS,
  },
};

export const VISUAL_LAYOUT_STORAGE_KEY = "simtile.gamepad-lab.visual-layouts.v1";

export function loadVisualLayoutOverrides(): VisualLayoutOverrides {
  try {
    const stored = localStorage.getItem(VISUAL_LAYOUT_STORAGE_KEY);
    if (!stored) return {};
    const parsed: unknown = JSON.parse(stored);
    return typeof parsed === "object" && parsed !== null ? parsed as VisualLayoutOverrides : {};
  } catch {
    return {};
  }
}

export function saveVisualLayoutOverrides(overrides: VisualLayoutOverrides): void {
  localStorage.setItem(VISUAL_LAYOUT_STORAGE_KEY, JSON.stringify(overrides));
}
